/**
 * Camera encoding policy for the mesh — the pure part.
 *
 * A mesh uploads one copy of the camera per peer. 720p at 1.7 Mbps is fine
 * toward one person; 3.4 Mbps toward two is not — most home uplinks throttle,
 * the encoder starts dropping frames toward everyone at once, and three
 * people get a worse call to keep a resolution nobody can see in a
 * three-tile grid. So from three participants up (two or more remote peers)
 * everyone sends 360p at 24 fps under a bitrate cap, and the per-peer cap
 * falls as the room grows. Plan Phase 4.
 *
 * Applied through sender parameters only — `scaleResolutionDownBy`,
 * `maxFramerate`, `maxBitrate` — never by re-opening the camera. The capture
 * stays what it is, so the local preview stays sharp, no driver blinks the
 * LED, and no track goes briefly muted. Phase 7's simulcast replaces this.
 */

export interface SendProfile {
  /** Target encode size; the encoder scales the capture down to it. */
  width: number
  height: number
  maxFramerate: number
  /** Bits per second toward ONE peer. Multiply by peers for the upstream cost. */
  maxBitrate: number
}

/** One-to-one: the Phase 7 'f' layer, so the SFU migration changes nothing here. */
export const FULL_PROFILE: SendProfile = {
  width: 1280,
  height: 720,
  maxFramerate: 30,
  maxBitrate: 1_700_000,
}

/** Three or more in the call. */
export const REDUCED_PROFILE: SendProfile = {
  width: 640,
  height: 360,
  maxFramerate: 24,
  maxBitrate: 650_000,
}

/** Four in the call — the mesh cap. Upstream stays under ~1.5 Mbps. */
export const CROWDED_PROFILE: SendProfile = { ...REDUCED_PROFILE, maxBitrate: 500_000 }

/** The camera profile for a mesh with this many REMOTE peers. */
export function cameraProfile(remotePeers: number): SendProfile {
  if (remotePeers <= 1) return FULL_PROFILE
  if (remotePeers === 2) return REDUCED_PROFILE
  return CROWDED_PROFILE
}

export interface CaptureSize {
  width?: number
  height?: number
}

/**
 * The encoder's downscale factor from what the camera captures to the
 * profile's size, by the longer side — so a phone's portrait 720×1280 lands
 * at 360×640 rather than at a 202-pixel sliver. Never below 1: the encoder
 * cannot upscale and browsers reject a factor under 1 outright. A capture
 * the browser has not measured yet is assumed at the full profile's size.
 */
export function scaleFactorFor(capture: CaptureSize | undefined, profile: SendProfile): number {
  const w = capture?.width ?? 0
  const h = capture?.height ?? 0
  const longSide =
    Math.max(w, h) > 0 ? Math.max(w, h) : Math.max(FULL_PROFILE.width, FULL_PROFILE.height)
  return Math.max(1, longSide / Math.max(profile.width, profile.height))
}

/** The fields of RTCRtpEncodingParameters we write. Typed loosely so this runs under vitest with no DOM. */
export interface EncodingLike {
  active?: boolean
  maxBitrate?: number
  maxFramerate?: number
  scaleResolutionDownBy?: number
}

/**
 * Write a profile into a sender's encodings, in place. Returns false when
 * there is nothing to write into: some browsers hand back no encodings until
 * the first negotiation completes, and setParameters on an empty list is a
 * no-op in one browser and a thrown error in another — the caller tries
 * again when the connection is up.
 */
export function applyProfile(
  encodings: EncodingLike[],
  profile: SendProfile,
  scaleFactor: number,
): boolean {
  if (encodings.length === 0) return false
  for (const encoding of encodings) {
    encoding.maxBitrate = profile.maxBitrate
    encoding.maxFramerate = profile.maxFramerate
    encoding.scaleResolutionDownBy = scaleFactor
  }
  return true
}

// ---------------------------------------------------------------- screen

export type ContentHint = 'motion' | 'detail' | 'text'

/**
 * The capture ceiling. A high-DPI display is captured at its physical
 * pixels — four times 1080p on some laptops — and every one of them has
 * to be encoded once per viewer and carried over the uplink. Nobody reads
 * a share at more than 1080p on the other side, so the browser scales the
 * capture down to it first. This is where most of the lag came from: a
 * whole-picture change (a new tab) is a keyframe-sized frame, and at four
 * times the pixels it took seconds to cross the wire while the voice
 * arrived on time.
 */
export const SCREEN_CAPTURE_MAX = { width: 1920, height: 1080 } as const

/**
 * How a screen share is encoded toward one peer (Phase 6). The opposite
 * trade-off from the camera: keep the resolution — a document that has
 * been shrunk is a document nobody can read — and let the encoder drop
 * frames instead when the link is short. Text and code want
 * 'maintain-resolution' and a low frame cap; a video playing in a tab wants
 * 'maintain-framerate' and the full 30.
 *
 * The bitrate is a BUDGET for the whole uplink, split across viewers,
 * because every peer gets its own copy: 2.5 Mbps to one viewer, but not
 * 2.5 to each of three — that is 7.5 Mbps nobody's home connection has,
 * and the queue it builds is measured in seconds. From three viewers up
 * the encoder also starts a step down, since three 1080p encodes is more
 * than a laptop wants to do while it also runs the meeting.
 */
export interface ScreenProfile {
  maxBitrate: number
  maxFramerate: number
  degradationPreference: 'maintain-resolution' | 'maintain-framerate'
  /** Encoder downscale from the capture before any adaptation. */
  scaleResolutionDownBy: number
}

/** The screen profile for a mesh with this many REMOTE peers, for content of this kind. */
export function screenProfile(remotePeers: number, hint: ContentHint): ScreenProfile {
  const maxBitrate = remotePeers <= 1 ? 2_500_000 : remotePeers === 2 ? 1_400_000 : 1_000_000
  const scaleResolutionDownBy = remotePeers >= 3 ? 1.5 : 1
  return hint === 'motion'
    ? {
        maxBitrate,
        maxFramerate: 30,
        degradationPreference: 'maintain-framerate',
        scaleResolutionDownBy,
      }
    : {
        maxBitrate,
        maxFramerate: 15,
        degradationPreference: 'maintain-resolution',
        scaleResolutionDownBy,
      }
}

/** The fields of RTCRtpSendParameters we write. Loose, so this runs under vitest with no DOM. */
export interface SendParametersLike {
  encodings: EncodingLike[]
  degradationPreference?: string
}

/**
 * Write a screen profile into a sender's parameters, in place, with the
 * adapter's extra downscale on top. False when there is nothing to write
 * into yet.
 */
export function applyScreenProfile(
  params: SendParametersLike,
  profile: ScreenProfile,
  adaptiveScale = 1,
): boolean {
  if (params.encodings.length === 0) return false
  for (const encoding of params.encodings) {
    encoding.maxBitrate = profile.maxBitrate
    encoding.maxFramerate = profile.maxFramerate
    encoding.scaleResolutionDownBy = profile.scaleResolutionDownBy * adaptiveScale
  }
  params.degradationPreference = profile.degradationPreference
  return true
}

export function sameScreenProfile(a: ScreenProfile | null, b: ScreenProfile | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.maxBitrate === b.maxBitrate &&
    a.maxFramerate === b.maxFramerate &&
    a.degradationPreference === b.degradationPreference &&
    a.scaleResolutionDownBy === b.scaleResolutionDownBy
  )
}

export type LimitationReason = 'none' | 'cpu' | 'bandwidth' | 'other'

/** The ladder the adapter walks: full size, two thirds, half. */
export const SCREEN_DOWNSCALE_STEPS: readonly number[] = [1, 1.5, 2]

/**
 * Meet's blur-then-sharpen, for one link. 'maintain-resolution' forbids the
 * browser from shrinking the picture on its own, which is right when the
 * link is fine and wrong when it is not: a link that cannot carry 1080p text
 * does not get sharper text later, it gets the same text seconds late. So
 * when the encoder says it is held back by bandwidth or by the CPU for two
 * polls in a row (about four seconds), step the resolution down one notch;
 * when it has been unconstrained for ten polls (about twenty seconds),
 * step back up. The frames get smaller, they arrive on time, and the text
 * softens for a while instead of freezing. Pure, so it is tested.
 */
export class ScreenAdapter {
  level = 0
  private bad = 0
  private good = 0

  constructor(
    private readonly stepDownAfter = 2,
    private readonly stepUpAfter = 10,
  ) {}

  /** Feed one poll's verdict. True when the level changed and the sender needs new parameters. */
  observe(reason: LimitationReason): boolean {
    if (reason === 'bandwidth' || reason === 'cpu') {
      this.good = 0
      this.bad++
      if (this.bad >= this.stepDownAfter && this.level < SCREEN_DOWNSCALE_STEPS.length - 1) {
        this.level++
        this.bad = 0
        return true
      }
      return false
    }
    this.bad = 0
    this.good++
    if (this.good >= this.stepUpAfter && this.level > 0) {
      this.level--
      this.good = 0
      return true
    }
    return false
  }

  get scale(): number {
    return SCREEN_DOWNSCALE_STEPS[this.level] ?? 1
  }

  /** A new share starts at full size. */
  reset(): void {
    this.level = 0
    this.bad = 0
    this.good = 0
  }
}

/** The shape of RTCRtpCodecCapability we read. */
export interface CodecLike {
  mimeType: string
  sdpFmtpLine?: string
}

/**
 * The order to offer codecs for a share. VP9 first: its screen-content
 * tools make a frame of text two to three times smaller than VP8's, and a
 * smaller frame is a frame that arrives sooner — which is the whole
 * problem. AV1 next (better still, dearer to encode), then the rest as the
 * browser had them. Retransmission and FEC entries keep their place after
 * the codecs. The peer that lacks the first choice negotiates the next; a
 * stable sort keeps the browser's order within each rank.
 */
export function preferScreenCodecs<T extends CodecLike>(codecs: readonly T[]): T[] {
  const rank = (c: T): number => {
    const mime = c.mimeType.toLowerCase()
    const fmtp = (c.sdpFmtpLine ?? '').toLowerCase()
    if (mime === 'video/vp9') return fmtp.includes('profile-id=2') ? 1 : 0
    if (mime === 'video/av1') return 2
    if (mime === 'video/vp8') return 3
    if (mime === 'video/h264') return 4
    if (mime === 'video/h265') return 5
    return 9
  }
  return codecs
    .map((c, i) => ({ c, i, r: rank(c) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map(({ c }) => c)
}

export type MediaPath = 'direct' | 'relay-udp' | 'relay-tcp' | 'relay-tls' | 'unknown'

/** The fields of a candidate stats report we read. */
export interface CandidateLike {
  candidateType?: string
  protocol?: string
  relayProtocol?: string
}

/**
 * How the packets travel, from the nominated candidate pair. A relay over
 * TCP or TLS is the one worth knowing about: it works where UDP is blocked
 * and it stalls the whole stream on every lost packet, so a share that lags
 * on such a path is the network, not the app.
 */
export function describePath(
  local: CandidateLike | undefined,
  remote: CandidateLike | undefined,
): MediaPath {
  if (!local && !remote) return 'unknown'
  const relay = [local, remote].find((c) => c?.candidateType === 'relay')
  if (!relay) return 'direct'
  const proto = (relay.relayProtocol ?? relay.protocol ?? 'udp').toLowerCase()
  if (proto === 'tls') return 'relay-tls'
  if (proto === 'tcp') return 'relay-tcp'
  return 'relay-udp'
}

export function sameProfile(a: SendProfile | null, b: SendProfile | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.width === b.width &&
    a.height === b.height &&
    a.maxFramerate === b.maxFramerate &&
    a.maxBitrate === b.maxBitrate
  )
}
